import { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Stack, ThemeProvider, Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { lerResumo, tituloResumo, type ResumoSemanal } from '../api/resumos'
import { ArvoreDiagnostico } from '../components/DiagnosticoCusto'
import { IndicadoresResumo } from '../components/IndicadoresResumo'
import { themesById } from '../theme'

const CLARO = themesById['financial-light']
const quando = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' })

/**
 * O resumo ou relatório pronto para imprimir ou salvar em PDF (o navegador faz o PDF):
 * página sem menu, sempre no tema claro, e a caixa de impressão abre sozinha.
 */
export function RelatorioImpressaoPage() {
  const { id = '' } = useParams()
  const [r, setR] = useState<ResumoSemanal | null | undefined>(undefined)
  const [erro, setErro] = useState<string | null>(null)
  const abriu = useRef(false)

  useEffect(() => {
    lerResumo(id)
      .then(setR)
      .catch((e: Error) => setErro(e.message))
  }, [id])

  useEffect(() => {
    if (!r || abriu.current) return
    abriu.current = true
    document.title = `${r.marca} — ${tituloResumo(r)}`
    // Depois do primeiro desenho, para a impressão pegar a página pronta.
    const t = window.setTimeout(() => window.print(), 400)
    return () => window.clearTimeout(t)
  }, [r])

  return (
    <ThemeProvider theme={CLARO}>
      <Box sx={{ bgcolor: '#ffffff', color: 'text.primary', minHeight: '100vh', '@media print': { minHeight: 0 } }}>
        <Box sx={{ maxWidth: 820, mx: 'auto', px: { xs: 2, sm: 4 }, py: 4, '@media print': { p: 0, maxWidth: 'none' } }}>
          {erro ? (
            <Alert severity="error">{erro}</Alert>
          ) : r === undefined ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : r === null ? (
            <Alert severity="warning">Resumo não encontrado ou fora das suas marcas.</Alert>
          ) : (
            <Relatorio r={r} />
          )}
        </Box>
      </Box>
    </ThemeProvider>
  )
}

function Secao({ titulo, itens }: { titulo: string; itens: string[] }) {
  if (!itens.length) return null
  return (
    <Box sx={{ breakInside: 'avoid' }}>
      <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
        {titulo}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
        {itens.map((t) => (
          <Typography component="li" variant="body2" key={t} sx={{ mb: 0.5 }}>
            {t}
          </Typography>
        ))}
      </Box>
    </Box>
  )
}

function Relatorio({ r }: { r: ResumoSemanal }) {
  const c = r.conteudo
  const mes = r.tipo === 'mes'
  return (
    <Stack spacing={2.5}>
      <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ '@media print': { display: 'none' } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
          Use "Salvar como PDF" na caixa de impressão.
        </Typography>
        <Button variant="contained" size="small" onClick={() => window.print()} sx={{ textTransform: 'none' }}>
          Imprimir ou salvar PDF
        </Button>
      </Stack>

      <Box>
        <Typography variant="caption" color="text.secondary">
          MarketingHUB · {mes ? 'Relatório mensal' : 'Resumo semanal'}
        </Typography>
        <Typography variant="h4" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.15 }}>
          {r.marca}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
          {tituloResumo(r)}
        </Typography>
      </Box>

      {c.demo ? <Alert severity="warning">Inclui dados de demonstração desta marca — números fictícios.</Alert> : null}

      <Typography variant="body1" sx={{ lineHeight: 1.65 }}>
        {c.resumo}
      </Typography>

      {c.indicadores?.length ? (
        <Box sx={{ breakInside: 'avoid' }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
            Indicadores · contra o período anterior, de mesmo tamanho
          </Typography>
          <IndicadoresResumo itens={c.indicadores} />
        </Box>
      ) : null}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' }, '@media print': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' } }}>
        <Secao titulo="Destaques" itens={c.destaques} />
        <Secao titulo="Atenção" itens={c.atencao} />
        <Secao titulo={mes ? 'Para o próximo mês' : 'Para esta semana'} itens={c.acoes} />
      </Box>

      {c.diagnostico?.length ? (
        <Stack spacing={1} sx={{ breakInside: 'avoid' }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
            Diagnóstico do custo por conversão
          </Typography>
          <ArvoreDiagnostico arvore={c.diagnostico} />
        </Stack>
      ) : null}

      {c.paineis.length ? (
        <Stack spacing={1}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
            Achados de cada painel
          </Typography>
          {c.paineis.map((p) => (
            <Box key={p.painel} sx={{ breakInside: 'avoid' }}>
              <Typography variant="subtitle2">{{ trafego_pago: 'Tráfego pago', organico: 'Orgânico', redes_sociais: 'Redes sociais' }[p.painel]}</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {p.achados.map((a) => (
                  <Typography component="li" variant="body2" color="text.secondary" key={a.texto}>
                    {a.texto}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      ) : null}

      <Typography variant="caption" color="text.secondary">
        {r.fonte === 'ia' ? 'Escrito pela IA, com cada número conferido contra os dados dos painéis.' : 'Montado pelo sistema a partir dos dados dos painéis.'} Gerado em{' '}
        {quando.format(new Date(r.geradoEm))}.
      </Typography>
    </Stack>
  )
}
